import React from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import Footer from '../components/Footer';

const PHOTOS = {
  regroup: '/assets/images/Personal Photos/2025-07-20_group-ride-mountain-roadside-2.png',
  townStop: '/assets/images/Personal Photos/2025-04-20_group-bikes-roadside-town-mexico.png',
  mountainBike: '/assets/images/Personal Photos/2024-09-28_aprilia-rs660-mt-charleston-road-wide.png',
  portrait: '/assets/images/Personal Photos/2024-07-21_daniel-with-rs660-graffiti-bridge.png',
  coastal: '/assets/images/Personal Photos/2025-03-27_two-bikes-coastal-overlook-baja.png',
  elTriunfo: '/assets/images/Personal Photos/2025-03-23_group-bikes-el-triunfo-baja.png',
  desertRoad: '/assets/images/Personal Photos/2025-05-01_desert-road-to-sea-baja.png',
};

const leadPhoto = {
  src: PHOTOS.elTriunfo,
  alt: 'Motorcycles parked in a group outside a roadside building',
  caption: 'Pit stop at El Triunfo, La Paz, BCS.',
};
const supportingPhotos = [
  { src: PHOTOS.coastal, alt: 'Two motorcycles parked at a coastal overlook', caption: 'Duo Ride to the Coast, San Felipe, BCS.' },
  { src: PHOTOS.townStop, alt: 'Sport motorcycles lined up on a town street', caption: 'Sport bikes ride out to Cabo San Lucas, BCS.' },
  { src: PHOTOS.desertRoad, alt: 'An empty desert road leading toward the horizon', caption: 'Baja transcontinental road toward the coast.' },
];

const About: React.FC = () => {
  const { colors, dsColors } = useTheme();

  const eyebrow: React.CSSProperties = {
    color: dsColors.treadGray,
    fontSize: '0.75rem',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.2em',
    marginBottom: '1rem',
  };
  const h2: React.CSSProperties = {
    color: colors.textPrimary,
    fontSize: 'clamp(1.5rem, 3vw, 2rem)',
    fontWeight: 700,
    marginBottom: '1.25rem',
    lineHeight: 1.2,
    textWrap: 'balance',
  };
  const body: React.CSSProperties = {
    color: colors.textSecondary,
    fontSize: '1rem',
    lineHeight: 1.7,
    textWrap: 'pretty',
  };
  const textLink: React.CSSProperties = {
    color: dsColors.primary,
    '--tw-ring-color': dsColors.primary,
  } as React.CSSProperties;

  return (
    <div className="min-h-screen" style={{ backgroundColor: colors.background }}>

      {/* Opening — copy beside substantial portrait */}
      <section className="pt-24 pb-12 px-4 sm:px-6 md:pt-32">
        <div className="max-w-5xl mx-auto grid lg:grid-cols-[11fr_9fr] gap-10 lg:gap-14 items-center">
          <div>
            <p style={eyebrow}>About RidePulse</p>
            <h1
              className="text-3xl sm:text-4xl md:text-5xl font-bold mb-6 leading-tight"
              style={{ color: colors.textPrimary, textWrap: 'balance' }}
            >
              The details make the difference.
            </h1>
            <p
              className="text-lg sm:text-xl font-semibold mb-4"
              style={{ color: colors.textPrimary }}
            >
              I&rsquo;m Paul, the founder of RidePulse.
            </p>
            <p style={body}>
              The app reflects what I wanted as someone joining events myself: a clearer plan, useful information about the group, and choices about what I share.
            </p>
          </div>
          <figure className="m-0">
            <img
              src={PHOTOS.portrait}
              alt="Paul with his motorcycle under a graffiti-covered bridge"
              className="w-full rounded-2xl object-cover"
              style={{ aspectRatio: '4 / 3', border: `1px solid ${colors.border}` }}
            />
            <figcaption className="mt-3 text-sm" style={{ color: colors.textSecondary }}>
              Bridge to nowhere, Charleston SC.
            </figcaption>
          </figure>
        </div>
      </section>

      {/* Remaining opening story */}
      <section className="pb-16 md:pb-20 px-4 sm:px-6">
        <div className="max-w-2xl mx-auto">
          <p className="text-base sm:text-lg leading-relaxed mb-5" style={{ color: colors.textSecondary, textWrap: 'pretty' }}>
            RidePulse grew out of the events I attended and the questions that came up before we set off. Who&rsquo;s leading? Where are we heading? Will my bike keep up?
          </p>
          <p className="text-base sm:text-lg leading-relaxed" style={{ color: colors.textSecondary, textWrap: 'pretty' }}>
            Those details shape whether an event is right for you. I wanted a place where they were part of the plan, rather than scattered across posts and messages.
          </p>
        </div>
      </section>

      {/* Section 1 — story with supporting photo */}
      <section className="pb-16 md:pb-24 px-4 sm:px-6">
        <div className="max-w-5xl mx-auto grid lg:grid-cols-[3fr_2fr] gap-10 lg:gap-14 items-center">
          <div>
            <h2 style={h2}>A familiar idea, built around motorcycles.</h2>
            <p style={{ ...body, marginBottom: '1rem' }}>
              Creating an event should feel familiar. The difference is what you can learn before deciding to join: who&rsquo;s hosting, what kind of event it is, and the riders and motorcycles taking part.
            </p>
            <p style={body}>
              The conversation belongs with the event too, so questions and updates have somewhere to go.
            </p>
          </div>
          <figure className="m-0">
            <img
              src={PHOTOS.regroup}
              alt="A line of motorcycles parked at a mountain roadside overlook"
              className="w-full rounded-2xl object-cover"
              style={{ aspectRatio: '4 / 3', border: `1px solid ${colors.border}` }}
            />
            <figcaption className="mt-3 text-sm" style={{ color: colors.textSecondary }}>
              Group Ride to Mt. Charleston, Las Vegas, NV.
            </figcaption>
          </figure>
        </div>
      </section>

      {/* Section 2 — privacy, text-led */}
      <section className="pb-16 md:pb-24 px-4 sm:px-6">
        <div className="max-w-2xl mx-auto">
          <h2 style={h2}>Useful information. Personal choices.</h2>
          <p style={{ ...body, marginBottom: '1rem' }}>
            I wanted to know enough about an event to decide whether to attend. I didn&rsquo;t want that to mean sharing everything about myself.
          </p>
          <p style={{ ...body, marginBottom: '1rem' }}>
            That distinction shaped RidePulse. Profile visibility, the motorcycle you bring, contact information, and live location sharing serve different purposes. They shouldn&rsquo;t all depend on one privacy switch.
          </p>
          <p style={{ ...body, marginBottom: '1.5rem' }}>
            For ride events, the meetup is available first. The destination and stops are revealed at check-in. That timing was a deliberate choice, not an afterthought.
          </p>
          <Link
            to="/privacy"
            className="text-sm font-semibold rounded focus:outline-none focus-visible:ring-2"
            style={textLink}
          >
            Privacy &amp; Visibility &rarr;
          </Link>
        </div>
      </section>

      {/* Section 3 — motorcycle, photo left */}
      <section className="pb-16 md:pb-24 px-4 sm:px-6">
        <div className="max-w-5xl mx-auto grid lg:grid-cols-[2fr_3fr] gap-10 lg:gap-14 items-center">
          <figure className="m-0 order-last lg:order-first">
            <img
              src={PHOTOS.mountainBike}
              alt="A sport motorcycle parked on a mountain road"
              className="w-full rounded-2xl object-cover"
              style={{ aspectRatio: '4 / 3', border: `1px solid ${colors.border}` }}
            />
          </figure>
          <div>
            <h2 style={h2}>The motorcycle is part of the event.</h2>
            <p style={{ ...body, marginBottom: '1rem' }}>
              Knowing what people are bringing helps make sense of the group. Your selected motorcycle becomes part of the event&rsquo;s participant information, while your other bikes follow your profile visibility choices.
            </p>
            <p style={{ ...body, marginBottom: '1.5rem' }}>
              The Garage also keeps useful specifications close at hand, including torque and tire sizes where catalog data is available. It isn&rsquo;t intended to be another maintenance tracker.
            </p>
            <Link
              to="/garage"
              className="text-sm font-semibold rounded focus:outline-none focus-visible:ring-2"
              style={textLink}
            >
              Explore the Garage &rarr;
            </Link>
          </div>
        </div>
      </section>

      {/* Gallery — lead photograph + supporting images */}
      <section className="pb-16 md:pb-24 px-4 sm:px-6">
        <div className="max-w-5xl mx-auto">
          <div className="max-w-2xl mb-8">
            <p style={body}>
              The photographs here are from my own riding. They give some context to the experiences behind the app.
            </p>
          </div>
          <figure className="m-0 mb-6">
            <img
              src={leadPhoto.src}
              alt={leadPhoto.alt}
              loading="lazy"
              className="w-full rounded-2xl object-cover"
              style={{ aspectRatio: '21 / 9', border: `1px solid ${colors.border}` }}
            />
            <figcaption className="mt-3 text-sm" style={{ color: colors.textSecondary }}>
              {leadPhoto.caption}
            </figcaption>
          </figure>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            {supportingPhotos.map((g) => (
              <figure key={g.src} className="m-0">
                <img
                  src={g.src}
                  alt={g.alt}
                  loading="lazy"
                  className="w-full rounded-2xl object-cover"
                  style={{ aspectRatio: '4 / 3', border: `1px solid ${colors.border}` }}
                />
                <figcaption className="mt-3 text-sm" style={{ color: colors.textSecondary }}>
                  {g.caption}
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* Quiet close */}
      <section className="pb-24 px-4 sm:px-6">
        <div className="max-w-2xl mx-auto text-center">
          <div className="flex items-center justify-center gap-6 flex-wrap">
            <Link
              to="/events"
              className="text-sm font-semibold rounded focus:outline-none focus-visible:ring-2"
              style={textLink}
            >
              See Events &rarr;
            </Link>
            <Link
              to="/contact"
              className="text-sm font-semibold rounded focus:outline-none focus-visible:ring-2"
              style={textLink}
            >
              Get in touch &rarr;
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default About;
